/* eslint-disable react/no-multi-comp */
import React, {
  Component,
  ComponentType,
  Fragment,
  FunctionComponent,
  useRef,
  useCallback,
  useEffect,
  useState,
  ReactNode,
} from 'react';
import PropTypes from 'prop-types';
import hoistStatics from 'hoist-non-react-statics';
import { getWindowOrThrow } from 'app/utils/env';
import { isShallowEqual } from 'app/utils/object';
import isEqual from 'lodash/isEqual';
import { warn } from 'app/services/logger';
import { useMemoOne } from 'app/utils/useMemoOne';

function getComponentName<P>(component: ComponentType<P>) {
  return component.displayName || component.name || 'Component';
}

type MakeMixinComponent<P1, P2> = (component: ComponentType<P1>) => ComponentType<P2>;

/**
 * A simple helper for defining React mixins. It takes mixin (a function that takes a component and
 * returns a component) and wraps the mixin so that the returned component has a `displayName` of
 * the form `MixinName(WrappedComponentName)` similar to the components returned by react-redux's
 * `connect`, and calls `hoistStatics` on the returned component.
 */
export const wrapMixin = <P1, P2>(
  makeMixinComponent: MakeMixinComponent<P1, P2>,
) => (WrappedComponent: ComponentType<P1>): ComponentType<P2> => {
  const WrapperComponent = makeMixinComponent(WrappedComponent);

  const wrapperName = getComponentName(WrapperComponent);
  const wrappedName = getComponentName(WrappedComponent);
  WrapperComponent.displayName = `${wrapperName}(${wrappedName})`;

  hoistStatics(WrapperComponent, WrappedComponent);

  return WrapperComponent;
};

// Useful for tests if you want to isolate updates to components
export class NoUpdate extends Component {
  public shouldComponentUpdate() { return false; }
  public render() { return this.props.children; }
}

// Useful for tests to listen for number of renders or changes to props.
export class OnRender extends Component<{ fn: () => void }> {
  public static propTypes = {
    fn: PropTypes.func.isRequired,
  };

  public componentDidMount() {
    this.props.fn();
  }

  public componentDidUpdate() {
    this.props.fn();
  }

  public render() {
    return null;
  }
}

/**
 * The `handler` function will be called on mouse click events and focus events that are outside of
 * the node returned by `getNode`.
 */
export const OnFocusOutside: FunctionComponent<{ getNode: () => Node | null, handler: () => void }> = (props) => {
  useOnFocusOutside({
    getNode: props.getNode,
    callback: props.handler,
  });

  return <Fragment>{props.children || null}</Fragment>;
};

export const useOnFocusOutside = (
  { getNode, callback }: { getNode: () => Node | null, callback: () => void },
) => {
  const alreadyTriggeredHandler = useRef(false);
  const getNodeRef = useRef(getNode);
  getNodeRef.current = getNode;
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  const eventListener = useCallback((event) => {
    if (!alreadyTriggeredHandler.current) {
      const node = getNodeRef.current();

      if (node && !node.contains(event.target)) {
        // Don't call the callback again until the next frame so that we only trigger the callback
        // once per user action.
        alreadyTriggeredHandler.current = true;
        setTimeout(() => {
          alreadyTriggeredHandler.current = false;
        }, 0);

        callbackRef.current();
      }
    }
  }, []);

  useEffect(() => {
    // Need to use `focusin` instead of `focus` because `focus` events do not bubble.
    getWindowOrThrow().addEventListener('focusin', eventListener, false);
    getWindowOrThrow().addEventListener('mousedown', eventListener, false);
    getWindowOrThrow().addEventListener('touchstart', eventListener, false);

    return () => {
      getWindowOrThrow().removeEventListener('focusin', eventListener, false);
      getWindowOrThrow().removeEventListener('mousedown', eventListener, false);
      getWindowOrThrow().removeEventListener('touchstart', eventListener, false);
    };
  }, [eventListener]);
};

/**
 * Useful if you want your children to be either a `ReactNode` or a function.
 *
 * Work-around for the fact that `ReactNode` contains `Fragment`
 * which contains `any` which makes `typeof children === 'function'
 * not enough to determine that `children` is `RenderProp`.
 */
export const isRenderProp = <RenderProp extends (...args: any[]) => any>(
  children: RenderProp | ReactNode,
): children is RenderProp =>
  typeof children === 'function';

/**
 * Similar to `useCallback` but the returned refernce is memoized regardless if
 * value for `f` changes.
 *
 * Unlike React's `useCallback`, the value returned by `useLatestCallback` is
 * guaranteed to never change.
 */
export function useLatestCallback<
  Args extends any[], Return
>(f: (...args: Args) => Return): (...args: Args) => Return {
  const ref = useLatest(f);
  // We don't use `useCallback` to define the wrapper function because React may
  // clear the memory of `useCallback` and `useMemo`, but we want to guarantee
  // that the value returned from `useLatestCallback` never changes.
  //
  // It's okay to use `ref` inside `wrapperFunction` because we know the value
  // of `ref` itself will never change.
  const wrapperFunction = useConstant(() => (...args: Args) => ref.current(...args));

  return wrapperFunction;
}

/**
 * Returns a ref containing the given value. Useful for using a value in
 * useEffect() callbacks when you don't want to trigger the useEffect() when the
 * value changes.
 *
 * Note that the ref will only be updating on useEffect(), so that it only
 * updated when React "commits" the virtual DOM for this component. This can be
 * important to prevent intermediate renders from affecting the behavior of side
 * effects that use this ref.
 */
export function useLatest<T>(value: T): { readonly current: T } {
  const ref = useRef<T>(value);
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref;
}

/**
 * Tracks if a component is mounted.
 * Returned value is a ref to avoid it being pulled to dependencies
 * of `useEffect` for example. Otherwise, `useEffect` would be
 * rererun just as the component is being unmounted.
 *
 * @returns mounted a reference value indicating if the component is mounted
 */
export function useIsMountedRef(): { readonly current: boolean } {
  const mounted = useRef(true);

  useEffect(() => {
    return () => {
      mounted.current = false;
    };
  }, []);

  return mounted;
}

interface IShallowMemoOptions {
  /**
   * Validate shallow equality check vs deep equality check. It
   * will show a warning in console if value is equal deeply but
   * not shallowly. This can be useful for detecting bugs when the
   * fields in the value passed in are not correctly memoized.
   */
  deepCheck?: boolean;
}

export function useShallowMemo<T>(value: T, { deepCheck }: IShallowMemoOptions = {}): T {
  return useMemoOne(
    () => value,
    [value],
    (a, b) => {
      if (!isShallowEqual(a, b)) {
        if (deepCheck && isEqual(a, b)) {
          warn([
            'Your component failed to memoize args passed to AutoLoader.',
            'Please make sure each argument is memoized.',
          ].join('\n'));
        } else {
          return false;
        }
      }

      return true;
    },
  );
}

export function useConstant<T>(initValue: () => T) {
  const [staticValue] = useState(initValue);

  return staticValue;
}

export type CallbackRef<Node = HTMLElement> = (node: Node | null) => void;

/**
 * Useful when you want to apply multiple callback refs to a single element.
 */
export function useCombineCallbackRefs<Node extends HTMLElement = HTMLElement>(
  callbackRefs: Array<CallbackRef<Node> | undefined>,
): CallbackRef<Node> {
  return useCallback(node => {
    for (const callbackRef of callbackRefs) {
      if (callbackRef) {
        callbackRef(node);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, callbackRefs);
}
